import React, { useState } from "react";
import { Input, Modal, notification } from "antd";
import { Loader2, Plus, Sparkles, X } from "lucide-react";
import WebUtils from "../../../../../utils/WebUtils";
import { generateAiResumeContentApi } from "../../../../../api/b2b-candidate";
import Utils from "../../../../../utils/Utils";

const { TextArea } = Input;

const extractBullets = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload.filter(Boolean);
  if (typeof payload === "string")
    return payload
      .split("\n")
      .map((b) => b.trim())
      .filter(Boolean);
  if (Array.isArray(payload.responsibilities))
    return payload.responsibilities.filter(Boolean);
  if (Array.isArray(payload.bullets)) return payload.bullets.filter(Boolean);
  if (Array.isArray(payload.highlights))
    return payload.highlights.filter(Boolean);
  return [];
};

function WriteWithAiBulletsModal({
  open,
  setOpen,
  item,
  bulletsField = "responsibilities",
  sectionKey = "experiences",
  infoContent,
  onApply,
}) {
  const userDetail = Utils.getUserDetail();

  const [instructions, setInstructions] = useState("");
  const [aiBullets, setAiBullets] = useState([]);
  const [generating, setGenerating] = useState(false);

  const hasGenerated = aiBullets.some((b) => b?.trim?.());
  const currentBullets = (item?.[bulletsField] ?? []).filter(Boolean);

  const handleClose = () => {
    setInstructions("");
    setAiBullets([]);
    setGenerating(false);
    setOpen(false);
  };

  const handleGenerate = async () => {
    let req = {};

    try {
      setGenerating(true);

      const baseItem = item ?? {};
      const currentSectionContent = hasGenerated
        ? {
            ...baseItem,
            [bulletsField]: aiBullets.filter((b) => b?.trim?.()),
          }
        : baseItem;
      if (userDetail?.userRole === "JOBDRONA_CANDIDATE") {
        req = {
          candidateContext: null,
          currentSectionKey: sectionKey,
          currentSectionContent,
          language: "English",
          instructions: instructions?.trim() ?? "",
          userIdentifier: userDetail?.userIdentifier,
        };
      } else {
        req = {
          organizationId: userDetail?.organizationId,
          candidateContext: null,
          currentSectionKey: sectionKey,
          currentSectionContent,
          language: "English",
          instructions: instructions?.trim() ?? "",
          userIdentifier: userDetail?.userIdentifier,
        };
      }

      const result = await WebUtils.httpOperations(
        generateAiResumeContentApi,
        req,
        "POST",
      );

      if (
        result?.data?.resultStatusInfo?.resultCode?.toLowerCase() === "success"
      ) {
        const bullets = extractBullets(
          result?.data?.data?.[sectionKey]?.[0]?.highlights,
        );
        setAiBullets(bullets);
        notification.success({
          message: "Success",
          description: "Bullets generated!",
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "Failed",
          description: result?.data?.resultStatusInfo?.message,
          placement: "topRight",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleApply = () => {
    onApply(aiBullets.map((b) => b?.trim?.()).filter(Boolean));
    handleClose();
  };

  const updateAiBullet = (idx, val) => {
    setAiBullets((prev) => prev.map((b, i) => (i === idx ? val : b)));
  };

  const removeAiBullet = (idx) => {
    setAiBullets((prev) => prev.filter((_, i) => i !== idx));
  };

  const addAiBullet = () => {
    setAiBullets((prev) => [...prev, ""]);
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={950}
    >
      <div className="flex items-center gap-3 mb-1">
        <div className="border border-[#E4E7EC] px-[.65rem] py-[.65rem] rounded-lg shadow">
          <Sparkles size={19} />
        </div>
        <div>
          <p className="text-[1.05rem] font-semibold text-[#0F1B23] mb-0">
            Tell AI what to improve
          </p>
          <p className="text-sm text-[#475467]">
            AI will rewrite the bullets for the entry below.
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-5">
        <div className="flex flex-col gap-2">
          <p className="text-[.85rem] font-medium text-[#344054]">
            Current Details
          </p>

          <div className="border rounded-lg p-3 bg-[#F9FAFB] flex flex-col gap-3 h-[300px] overflow-y-auto">
            {infoContent}

            <div className="border-t pt-2">
              <p className="text-[.75rem] uppercase tracking-wide text-[#667085] mb-2">
                Existing bullets
              </p>
              {currentBullets.length > 0 ? (
                <ul className="list-disc pl-5 flex flex-col gap-1">
                  {currentBullets.map((b, i) => (
                    <li key={i} className="text-[.82rem] text-[#344054]">
                      {b}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[.8rem] text-[#98A2B3] italic mb-0">
                  No bullets yet
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-[.85rem] font-medium text-[#344054]">
            AI Generated Bullets
          </p>

          <div className="border rounded-lg p-3 flex flex-col gap-2 h-[300px] overflow-y-auto">
            {aiBullets.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-[.82rem] text-[#98A2B3] italic py-8">
                {generating
                  ? "Generating..."
                  : "AI generated bullets will appear here"}
              </div>
            ) : (
              aiBullets.map((b, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <TextArea
                    value={b}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    onChange={(e) => updateAiBullet(i, e.target.value)}
                    placeholder="Bullet text"
                    className="text-[.82rem]"
                  />
                  <button
                    type="button"
                    onClick={() => removeAiBullet(i)}
                    className="text-gray-400 hover:text-red-600 mt-2"
                    aria-label="Remove bullet"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))
            )}

            {hasGenerated && (
              <button
                type="button"
                onClick={addAiBullet}
                className="self-start flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-1"
              >
                <Plus size={14} /> Add bullet
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5">
        <p className="text-[.85rem] font-medium text-[#344054] mb-1">
          Describe changes you would like{" "}
        </p>
        <TextArea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Highlight leadership, ownership, and measurable achievements."
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      </div>
      {userDetail?.userRole === "JOBDRONA_CANDIDATE" && (
        <div className="mt-3 text-red-500">
          <strong>Note</strong> :{" "}
          <span>
            <strong>1</strong> credits will be detected for every AI generation
          </span>
        </div>
      )}

      <div className="flex justify-end items-center gap-3 mt-6">
        {!hasGenerated ? (
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="btn-orange px-16 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {generating ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Sparkles size={16} />
            )}
            {generating ? "Generating..." : "Generate with AI"}
          </button>
        ) : (
          <>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn-white px-16 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {generating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Sparkles size={16} />
              )}
              {generating ? "Regenerating..." : "Regenerate"}
            </button>
            <button
              onClick={handleApply}
              disabled={generating || !hasGenerated}
              className="btn-orange px-16 py-2 rounded-lg disabled:opacity-60"
            >
              Apply Changes
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}

export default WriteWithAiBulletsModal;
